import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import {
  type ConnectorPlatform,
  type SyncCadence,
  SYNC_CADENCE_DESCRIPTIONS,
  SYNC_CADENCE_LABELS,
  SYNC_CADENCE_OPTIONS,
} from '@/entities/media-connector';
import { FormSidePanel } from '@/shared/ui/layout/FormSidePanel';
import type { AddAccountSubmitPayload } from '../AddAccountDialog';
import { AddAccountAuthMethodFields } from './AddAccountAuthMethodFields';
import { AddAccountPlatformFields } from './AddAccountPlatformFields';
import { AddAccountTagsField } from './AddAccountTagsField';
import { useAddAccountForm } from './useAddAccountForm';

export interface AddAccountSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platformLabel: string;
  platform: ConnectorPlatform;
  isSubmitting?: boolean;
  onSubmit: (payload: AddAccountSubmitPayload) => void;
}

/**
 * Right-side counterpart to `AddAccountDialog`, used only by the redesigned
 * `MediaConnectorPane`. Carries every capability of the dialog — connection
 * method, tags, sync cadence, the token/service-account credential fields
 * and the advertiser/manager-account fields — through the same
 * `useAddAccountForm` hook, so the two surfaces cannot drift on validation
 * or submit behaviour. `AddAccountDialog` itself stays untouched and keeps
 * serving the pre-redesign detail page.
 *
 * There is no prefill here to seed on mount: unlike an edit form, every
 * field starts blank and `useAddAccountForm` resets them on close, so no
 * remount `key` is needed for a changed prop the way `GuidelineFormSheet`
 * needs one for a changed `guideline`.
 */
export function AddAccountSheet({
  open,
  onOpenChange,
  platformLabel,
  platform,
  isSubmitting,
  onSubmit,
}: AddAccountSheetProps) {
  const { t } = useTranslation('knowledge');
  const form = useAddAccountForm({ platform, onSubmit, onOpenChange });

  return (
    <FormSidePanel
      open={open}
      onOpenChange={form.handleClose}
      title={t('mediaConnectors.addAccountDialog.addPlatformAccount', { platform: platformLabel })}
      className="w-[560px] gap-sm"
      footer={
        <div className="flex items-center gap-xs">
          <Button
            variant="outline"
            size="lg"
            onClick={() => form.handleClose(false)}
            disabled={isSubmitting}
          >
            {t('mediaConnectors.addAccountDialog.cancel')}
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={form.handleSubmit}
            disabled={!form.isValid || isSubmitting}
          >
            {isSubmitting
              ? t('mediaConnectors.addAccountDialog.connecting')
              : t('mediaConnectors.addAccountDialog.connect')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-md">
        <AddAccountAuthMethodFields
          supportedMethods={form.supportedMethods}
          hasMultipleMethods={form.hasMultipleMethods}
          authMethod={form.authMethod}
          onAuthMethodChange={form.setAuthMethod}
          token={form.token}
          onTokenChange={form.setToken}
          keyFileName={form.keyFileName}
          onFileChange={form.handleFileChange}
          fileInputRef={form.fileInputRef}
          disabled={isSubmitting}
        />

        <div className="space-y-1.5">
          <Label htmlFor="sheet-account-title">{t('mediaConnectors.addAccountDialog.title')}</Label>
          <Input
            id="sheet-account-title"
            placeholder={t('mediaConnectors.addAccountDialog.egUsMarketApac')}
            value={form.title}
            onChange={(e) => form.setTitle(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <AddAccountTagsField
          tags={form.tags}
          tagInput={form.tagInput}
          onTagInputChange={form.setTagInput}
          onKeyDown={form.handleTagKeyDown}
          onBlur={form.handleTagBlur}
          onRemoveTag={form.removeTag}
          disabled={isSubmitting}
        />

        <div className="space-y-1.5">
          <Label htmlFor="sheet-sync-cadence">
            {t('mediaConnectors.addAccountDialog.syncFrequency')}
          </Label>
          <Select
            value={form.syncCadence}
            onValueChange={(v) => form.setSyncCadence(v as SyncCadence)}
            disabled={isSubmitting}
          >
            <SelectTrigger id="sheet-sync-cadence" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SYNC_CADENCE_OPTIONS.map((cadence) => (
                <SelectItem key={cadence} value={cadence}>
                  {SYNC_CADENCE_LABELS[cadence]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {SYNC_CADENCE_DESCRIPTIONS[form.syncCadence]}
          </p>
        </div>

        <AddAccountPlatformFields
          needsAdvertiser={form.needsAdvertiser}
          advertiserId={form.advertiserId}
          onAdvertiserIdChange={form.setAdvertiserId}
          platformLabel={platformLabel}
          usesGoogleAdsApi={form.usesGoogleAdsApi}
          loginCustomerId={form.loginCustomerId}
          onLoginCustomerIdChange={form.setLoginCustomerId}
          asksForCm360Account={form.asksForCm360Account}
          cm360AccountId={form.cm360AccountId}
          onCm360AccountIdChange={form.setCm360AccountId}
          disabled={isSubmitting}
        />
      </div>
    </FormSidePanel>
  );
}

AddAccountSheet.displayName = 'AddAccountSheet';
